/**
 * Feature request triage.
 *
 * The badge on the nav item counts requests that have ARRIVED SINCE THIS ADMIN
 * LAST LOOKED, not `status === 'new'`. Conduit learned this the hard way: if the
 * badge counts untriaged items, the only way to clear it is to change a
 * request's status, which forces "I have read this" and "I have decided what to
 * do about this" to be the same action. You then either triage prematurely to
 * silence a number, or live with a badge that never clears. Both numbers are
 * shown here — "new since you looked" is the notification, `untriaged` is the
 * actual work queue.
 *
 * The watermark is per-admin, so one person reading the queue does not clear
 * everyone else's badge, and it is set to the moment the list was FETCHED rather
 * than the moment of the click — otherwise a request that lands while the page
 * is open gets marked seen having never been on screen.
 */

import { useEffect, useState } from 'react';
import {
  listFeatureRequests, updateFeatureRequest, deleteFeatureRequest,
  markFeatureRequestsSeen, type FeatureRequest, type FeatureStatus,
} from './api';

const STATUSES: FeatureStatus[] = ['new', 'planned', 'in-progress', 'shipped', 'declined'];

const LABEL: Record<FeatureStatus, string> = {
  new: 'Received',
  planned: 'Planned',
  'in-progress': 'Being built',
  shipped: 'Shipped',
  declined: 'Not planned',
};

const COLOR: Record<FeatureStatus, string> = {
  new: 'var(--text-muted)',
  planned: 'var(--blue)',
  'in-progress': 'var(--yellow)',
  shipped: 'var(--green)',
  declined: 'var(--text-muted)',
};

export function FeaturesView() {
  const [rows, setRows] = useState<FeatureRequest[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState<FeatureStatus | 'all'>('all');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});

  async function load() {
    try {
      const r = await listFeatureRequests({});
      setRows(r.requests);
      setCounts(r.counts as unknown as Record<string, number>);
      // Marked seen up to the fetch time, not now.
      await markFeatureRequestsSeen({ at: r.fetchedAt });
      window.dispatchEvent(new CustomEvent('features-seen'));
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'Could not load the queue.');
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  async function setStatus(id: string, status: FeatureStatus) {
    setRows((r) => (r ?? []).map((x) => (x.id === id ? { ...x, status } : x)));
    try { await updateFeatureRequest({ id, status }); } catch { void load(); }
  }

  async function saveNote(id: string) {
    try { await updateFeatureRequest({ id, adminNote: note[id] ?? '' }); } catch { /* shown on reload */ }
  }

  async function remove(id: string) {
    // 'declined' is the normal close. Deleting loses the record that it was
    // already considered, so the same idea arrives again next month.
    if (!confirm('Delete this request? Declining it keeps the record — deleting means the same idea comes back with no history.')) return;
    setRows((r) => (r ?? []).filter((x) => x.id !== id));
    try { await deleteFeatureRequest({ id }); } catch { void load(); }
  }

  const shown = (rows ?? []).filter((r) => filter === 'all' || r.status === filter);

  return (
    <div>
      {error && <div className="banner banner-error">{error}</div>}

      <div className="fr-filters">
        <button className={'chip' + (filter === 'all' ? ' sel' : '')} onClick={() => setFilter('all')}>
          All <span className="chip-n">{rows?.length ?? 0}</span>
        </button>
        {STATUSES.map((s) => (
          <button key={s} className={'chip' + (filter === s ? ' sel' : '')} onClick={() => setFilter(s)}>
            {LABEL[s]} <span className="chip-n">{counts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      {rows === null ? (
        <div className="empty">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="empty">Nothing here.</div>
      ) : (
        <div className="fr-admin-list">
          {shown.map((r) => (
            <div className="card fr-admin" key={r.id}>
              <div className="fr-admin-hd">
                <div className="fr-admin-votes" title="Votes across all tenants">
                  {r.voteCount ?? 0}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="fr-admin-title">{r.title}</div>
                  <div className="fr-admin-meta mono dim">
                    {r.orgName || r.orgId || 'unknown org'} · {r.email || 'unknown'}
                    {r.appVersion ? ` · v${r.appVersion}` : ''}
                    {r.view ? ` · on ${r.view}` : ''}
                    {' · '}
                    {new Date(r.createdAt).toLocaleString()}
                  </div>
                </div>
                <span className="fr-pill" style={{ color: COLOR[r.status] }}>{LABEL[r.status]}</span>
              </div>

              <p className="fr-admin-details">{r.details}</p>

              <div className="fr-admin-actions">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    className={'chip' + (r.status === s ? ' sel' : '')}
                    onClick={() => void setStatus(r.id, s)}
                  >
                    {LABEL[s]}
                  </button>
                ))}
                <button className="btn-link danger" onClick={() => void remove(r.id)}>Delete</button>
              </div>

              <div className="fr-admin-note">
                <input
                  className="field-input"
                  placeholder="Internal note — never shown to the submitter"
                  defaultValue={r.adminNote ?? ''}
                  onChange={(e) => setNote((n) => ({ ...n, [r.id]: e.target.value }))}
                  onBlur={() => void saveNote(r.id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
